import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSeller } from '../../context/SellerContext';
import { sellerDashboardAPI, handleAPIError } from '../../utils/sellerAPI';

const SellerDashboard = () => {
  const navigate = useNavigate();
  const { seller, authToken, logout } = useSeller();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalProducts: 0,
    revenue: 0,
    rating: 0,
  });

  useEffect(() => {
    if (!authToken) {
      navigate('/seller/register');
      return;
    }
    fetchDashboardData();
  }, [authToken, navigate]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, statsRes] = await Promise.all([
        sellerDashboardAPI.getDashboard(),
        sellerDashboardAPI.getStats(),
      ]);

      setDashboardData(dashboardRes.data);
      setStats(statsRes.data);
    } catch (err) {
      const apiError = handleAPIError(err);
      console.error('Dashboard Error:', apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/seller/login');
  };

  const handleAddSignature = () => {
    navigate('/seller/settings?tab=signature');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      {/* Header Removed - Managed by Sidebar/Layout */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
        <p className="text-gray-600">Welcome back, {seller?.storeName || 'Seller'}!</p>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Alert Section */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8 rounded">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-yellow-800 font-semibold">⚠️ Your E-Signature is missing!</p>
              <p className="text-yellow-700 text-sm mt-1">
                Add your signature to complete your seller profile.
              </p>
            </div>
            <button
              onClick={handleAddSignature}
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg font-medium transition"
            >
              Add Signature
            </button>
          </div>
        </div>

        {/* Main Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-gray-600 text-sm font-medium mb-2">Total Orders</p>
            <p className="text-3xl font-bold text-gray-800">{stats.totalOrders}</p>
            <p className="text-green-600 text-sm mt-2">↑ View all orders</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-gray-600 text-sm font-medium mb-2">Total Products</p>
            <p className="text-3xl font-bold text-gray-800">{stats.totalProducts}</p>
            <p className="text-blue-600 text-sm mt-2">↑ Manage products</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-gray-600 text-sm font-medium mb-2">Revenue</p>
            <p className="text-3xl font-bold text-gray-800">₹{stats.revenue}</p>
            <p className="text-green-600 text-sm mt-2">↑ View payments</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-gray-600 text-sm font-medium mb-2">Rating</p>
            <p className="text-3xl font-bold text-gray-800">{stats.rating}/5</p>
            <p className="text-blue-600 text-sm mt-2">↑ View reviews</p>
          </div>
        </div>

        {/* Getting Started Section */}
        <div className="bg-white rounded-lg shadow p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Let's get your business started in 3 steps
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="border-2 border-blue-200 rounded-lg p-6 hover:shadow-lg transition cursor-pointer">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">Upload Catalogs</h3>
                <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-semibold">Step 1</span>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Upload your product catalogs to get started selling
              </p>
              <a href="/seller/catalog-upload" className="text-blue-600 hover:underline font-semibold text-sm">
                Get Started →
              </a>
            </div>

            <div className="border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg transition cursor-pointer">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">Add Products</h3>
                <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm font-semibold">Step 2</span>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Add individual products or bulk import your inventory
              </p>
              <a href="/seller/products/add" className="text-gray-600 hover:text-gray-800 font-semibold text-sm">
                Get Started →
              </a>
            </div>

            <div className="border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg transition cursor-pointer">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">Start Selling</h3>
                <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm font-semibold">Step 3</span>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Receive orders from customers across India
              </p>
              <a href="/seller/orders" className="text-gray-600 hover:text-gray-800 font-semibold text-sm">
                View Orders →
              </a>
            </div>
          </div>
        </div>

        {/* Account Setup Checklist */}
        <div className="bg-white rounded-lg shadow p-8 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-6">Account Setup Checklist</h2>

          <div className="space-y-3">
            <div className="flex items-center p-4 border border-gray-200 rounded-lg">
              <input type="checkbox" className="w-5 h-5 text-green-600" defaultChecked />
              <span className="ml-4 font-medium text-gray-800">Mobile Number Verified</span>
            </div>

            <div className="flex items-center p-4 border border-gray-200 rounded-lg">
              <input type="checkbox" className="w-5 h-5 text-green-600" defaultChecked />
              <span className="ml-4 font-medium text-gray-800">Business Details Added</span>
            </div>

            <div className="flex items-center p-4 border border-gray-200 rounded-lg">
              <input type="checkbox" className="w-5 h-5" />
              <span className="ml-4 font-medium text-gray-800">Set Password</span>
            </div>

            <div className="flex items-center p-4 border border-gray-200 rounded-lg">
              <input type="checkbox" className="w-5 h-5" />
              <span className="ml-4 font-medium text-gray-800">Add Business Type</span>
            </div>

            <div className="flex items-center p-4 border border-gray-200 rounded-lg">
              <input type="checkbox" className="w-5 h-5" />
              <span className="ml-4 font-medium text-gray-800">E-Signature Uploaded</span>
            </div>
          </div>
        </div>

        {/* Learning Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Learn & Grow On Kalinga</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <a href="/learning/getting-started" className="bg-white rounded-lg p-6 hover:shadow-lg transition">
              <h3 className="font-bold text-gray-800 mb-2">Getting Started</h3>
              <p className="text-gray-600 text-sm">Learn the basics of selling on Kalinga</p>
            </a>

            <a href="/learning/pricing" className="bg-white rounded-lg p-6 hover:shadow-lg transition">
              <h3 className="font-bold text-gray-800 mb-2">Pricing Strategy</h3>
              <p className="text-gray-600 text-sm">Set competitive prices for your products</p>
            </a>

            <a href="/learning/marketing" className="bg-white rounded-lg p-6 hover:shadow-lg transition">
              <h3 className="font-bold text-gray-800 mb-2">Marketing Tips</h3>
              <p className="text-gray-600 text-sm">Grow your sales with effective marketing</p>
            </a>

            <a href="/learning/support" className="bg-white rounded-lg p-6 hover:shadow-lg transition">
              <h3 className="font-bold text-gray-800 mb-2">Customer Support</h3>
              <p className="text-gray-600 text-sm">Best practices for customer service</p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerDashboard;
